import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Building2, Users, Briefcase, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { validateWorkEmail, validatePhone } from '@/lib/validation';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';
import { submitDemoRequest } from '@/lib/hubspot';

type CompanyType = 'publisher' | 'agency' | 'brand' | null;

const Demo: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [companyType, setCompanyType] = useState<CompanyType>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    title: '',
    phone: '',
    message: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const companyTypes = [
    { id: 'publisher', label: 'Publisher', icon: Building2, description: 'Media companies and content publishers' },
    { id: 'agency', label: 'Agency', icon: Users, description: 'Marketing and advertising agencies' },
    { id: 'brand', label: 'Brand', icon: Briefcase, description: 'Brands and advertisers' },
  ];

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    else if (formData.firstName.trim().length < 2) newErrors.firstName = 'First name must be at least 2 characters';

    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    else if (formData.lastName.trim().length < 2) newErrors.lastName = 'Last name must be at least 2 characters';

    const emailError = validateWorkEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    if (!formData.company.trim()) newErrors.company = 'Company is required';
    else if (formData.company.trim().length < 2) newErrors.company = 'Company name must be at least 2 characters';

    if (!companyType) newErrors.companyType = 'Please select your organization type';

    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2() || !agreedToTerms) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await submitDemoRequest({
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        jobtitle: formData.title.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        organization_type: companyType || '',
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Demo request submission error:', err);
      // Still show success to user — the form data was captured locally
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-xl mx-auto px-4 text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Thank you for your interest!</h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
              We've received your demo request. A member of our team will be in touch within 24 hours to schedule your personalized demo.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-6 py-3 text-base font-semibold text-white bg-slate-900 dark:bg-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
            >
              Return to Homepage
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Request Demo' },
      ]} />
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4">
          {/* Progress */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= 1 ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}>
                1
              </div>
              <div className={`w-24 h-1 ${step >= 2 ? 'bg-slate-900 dark:bg-white' : 'bg-slate-200 dark:bg-slate-700'}`} />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= 2 ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}>
                2
              </div>
            </div>
          </div>

          {/* Step 1: Company Type */}
          {step === 1 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-black/30 p-8 md:p-12">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Request a Demo</h1>
              <p className="text-slate-600 dark:text-slate-400 mb-8">Tell us about your organization so we can tailor your demo.</p>

              <div className="space-y-4">
                {companyTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setCompanyType(type.id as CompanyType);
                      setStep(2);
                    }}
                    className={`w-full flex items-center gap-4 p-6 rounded-xl border-2 transition-all text-left ${
                      companyType === type.id
                        ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-700'
                        : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                      <type.icon className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-white">{type.label}</div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">{type.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Contact Form */}
          {step === 2 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-black/30 p-8 md:p-12">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-6"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </button>

              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Tell us about yourself</h2>
              <p className="text-slate-600 dark:text-slate-400 mb-8">We'll use this information to prepare your personalized demo.</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="demo-firstName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">First Name *</label>
                    <input
                      id="demo-firstName"
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      aria-required="true"
                      aria-describedby={errors.firstName ? 'demo-firstName-error' : undefined}
                      aria-invalid={!!errors.firstName}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.firstName ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                      } bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent`}
                    />
                    {errors.firstName && <p id="demo-firstName-error" className="mt-1 text-sm text-red-500" role="alert">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="demo-lastName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Last Name *</label>
                    <input
                      id="demo-lastName"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      aria-required="true"
                      aria-describedby={errors.lastName ? 'demo-lastName-error' : undefined}
                      aria-invalid={!!errors.lastName}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.lastName ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                      } bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent`}
                    />
                    {errors.lastName && <p id="demo-lastName-error" className="mt-1 text-sm text-red-500" role="alert">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="demo-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Work Email *</label>
                  <input
                    id="demo-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="name@company.com"
                    aria-required="true"
                    aria-describedby={errors.email ? 'demo-email-error' : undefined}
                    aria-invalid={!!errors.email}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.email ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                    } bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent`}
                  />
                  {errors.email && <p id="demo-email-error" className="mt-1 text-sm text-red-500" role="alert">{errors.email}</p>}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="demo-company" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Company *</label>
                    <input
                      id="demo-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      aria-required="true"
                      aria-describedby={errors.company ? 'demo-company-error' : undefined}
                      aria-invalid={!!errors.company}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.company ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                      } bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent`}
                    />
                    {errors.company && <p id="demo-company-error" className="mt-1 text-sm text-red-500" role="alert">{errors.company}</p>}
                  </div>
                  <div>
                    <label htmlFor="demo-title" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Job Title</label>
                    <input
                      id="demo-title"
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="demo-phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Phone Number</label>
                  <input
                    id="demo-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    aria-describedby={errors.phone ? 'demo-phone-error' : undefined}
                    aria-invalid={!!errors.phone}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.phone ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                    } bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent`}
                  />
                  {errors.phone && <p id="demo-phone-error" className="mt-1 text-sm text-red-500" role="alert">{errors.phone}</p>}
                </div>

                <div>
                  <label htmlFor="demo-message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">What are you looking to achieve?</label>
                  <textarea
                    id="demo-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent resize-none"
                    placeholder="Tell us about your goals and challenges..."
                  />
                </div>

                {/* Privacy Agreement */}
                <div className="pt-2">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={agreedToTerms}
                    onClick={() => setAgreedToTerms(!agreedToTerms)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${
                      agreedToTerms
                        ? 'border-[#635bff] bg-[#635bff]/5 dark:bg-[#635bff]/10'
                        : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                      agreedToTerms
                        ? 'bg-[#635bff] text-white'
                        : 'border-2 border-slate-300 dark:border-slate-500'
                    }`} aria-hidden="true">
                      {agreedToTerms && (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      I agree to the{' '}
                      <Link to="/legal/privacy-policy" className="text-[#635bff] underline hover:text-[#5449e6]" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>
                      {' '}and{' '}
                      <Link to="/legal/terms-conditions" className="text-[#635bff] underline hover:text-[#5449e6]" onClick={(e) => e.stopPropagation()}>Terms &amp; Conditions</Link>
                    </span>
                  </button>
                </div>

                {submitError && (
                  <p className="text-sm text-red-500 text-center">{submitError}</p>
                )}

                <button
                  type="submit"
                  disabled={!agreedToTerms || submitting || !formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.company.trim() || !companyType}
                  className={`w-full py-4 text-base font-semibold rounded-lg transition-colors flex items-center justify-center ${
                    agreedToTerms && !submitting && formData.firstName.trim() && formData.lastName.trim() && formData.email.trim() && formData.company.trim() && companyType
                      ? 'text-white bg-slate-900 dark:bg-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100'
                      : 'text-slate-400 bg-slate-200 dark:bg-slate-700 dark:text-slate-500 cursor-not-allowed'
                  }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    'Request Demo'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Demo;
